import React, { useState, useRef, useEffect, useCallback } from "react";

// Same external interface as the old <input type="date"> wrapper — all consumers unchanged
type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, "type">;

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const DAYS = ["Su","Mo","Tu","We","Th","Fr","Sa"];

function parseISO(val?: string | number | readonly string[]): Date | null {
  if (typeof val !== "string" || !val) return null;
  const [y, m, d] = val.split("-").map(Number);
  if (!y || !m || !d || isNaN(y) || isNaN(m) || isNaN(d)) return null;
  return new Date(y, m - 1, d);
}

function toISO(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function formatDisplay(iso: string): string {
  const d = parseISO(iso);
  if (!d) return "";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

function todayISO(): string {
  const t = new Date();
  return toISO(t.getFullYear(), t.getMonth(), t.getDate());
}

const POPUP_HEIGHT = 320; // approximate calendar height in px

export const DateInput = ({
  className = "",
  value,
  onChange,
  min,
  max,
  name,
  id,
  required,
  disabled,
  placeholder,
}: Props) => {
  const selected = (value as string) || "";
  const minISO = (min as string) || "";
  const maxISO = (max as string) || "";

  const initialDate = parseISO(selected) ?? new Date();
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(initialDate.getFullYear());
  const [month, setMonth] = useState(initialDate.getMonth());
  const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({});

  const inputRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  // Sync year/month when value changes externally
  useEffect(() => {
    const d = parseISO(selected);
    if (d) { setYear(d.getFullYear()); setMonth(d.getMonth()); }
  }, [selected]);

  const computePopupStyle = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const popupWidth = Math.max(288, rect.width);
    // Clamp so the popup never spills past the right edge of the viewport —
    // rect.left alone assumes there's always room to the right, which breaks
    // for inputs positioned mid-page on narrow screens.
    const margin = 8;
    const left = Math.min(rect.left, window.innerWidth - popupWidth - margin);
    const base: React.CSSProperties = {
      position: "fixed",
      left: Math.max(margin, left),
      width: popupWidth,
      zIndex: 9999,
    };
    if (spaceBelow >= POPUP_HEIGHT || spaceBelow >= window.innerHeight / 2) {
      setPopupStyle({ ...base, top: rect.bottom + 6 });
    } else {
      setPopupStyle({ ...base, bottom: window.innerHeight - rect.top + 6 });
    }
  }, []);

  const openCalendar = () => {
    if (disabled) return;
    computePopupStyle();
    setOpen(true);
  };

  // Close on outside click or scroll/resize
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (
        popupRef.current && !popupRef.current.contains(e.target as Node) &&
        inputRef.current && !inputRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const closeOnScroll = () => setOpen(false);
    document.addEventListener("mousedown", close);
    window.addEventListener("scroll", closeOnScroll, true);
    window.addEventListener("resize", closeOnScroll);
    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("scroll", closeOnScroll, true);
      window.removeEventListener("resize", closeOnScroll);
    };
  }, [open]);

  const emit = (iso: string) => {
    if (!onChange) return;
    onChange({
      target: { value: iso, name: name ?? "" } as HTMLInputElement,
      currentTarget: { value: iso, name: name ?? "" } as HTMLInputElement,
      nativeEvent: new Event("change"),
      bubbles: true, cancelable: false, defaultPrevented: false,
      eventPhase: 0, isTrusted: false, timeStamp: 0, type: "change",
      preventDefault: () => {}, isDefaultPrevented: () => false,
      stopPropagation: () => {}, isPropagationStopped: () => false,
      persist: () => {},
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const prevMonth = () => {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
  };

  const today = todayISO();
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = Array(firstDow).fill(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const years = Array.from({ length: 120 }, (_, i) => new Date().getFullYear() - 119 + i).reverse();

  return (
    <>
      {/* Trigger */}
      <div ref={inputRef} className="relative">
        <input
          readOnly
          id={id}
          name={name}
          required={required}
          disabled={disabled}
          value={formatDisplay(selected)}
          placeholder={placeholder ?? "DD/MM/YYYY"}
          onClick={openCalendar}
          className={`cursor-pointer ${selected ? "pr-14" : ""} ${className}`}
        />
        {selected && !disabled && (
          <button
            type="button"
            onClick={() => emit("")}
            className="absolute right-7 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Clear date"
            title="Clear date"
          >
            <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        )}
        <span
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          aria-hidden
        >
          <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18" />
          </svg>
        </span>
      </div>

      {/* Calendar popup — rendered via fixed positioning to escape modal overflow clipping */}
      {open && (
        <div
          ref={popupRef}
          style={popupStyle}
          className="bg-white border border-slate-200 rounded-2xl shadow-2xl p-4 w-72"
        >
          {/* Header */}
          <div className="flex items-center gap-1 mb-3">
            <button
              type="button"
              onClick={prevMonth}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors flex-shrink-0"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="flex-1 flex items-center justify-center gap-1.5">
              <select
                value={month}
                onChange={e => setMonth(Number(e.target.value))}
                className="text-xs font-semibold text-slate-700 bg-transparent border-none outline-none cursor-pointer"
              >
                {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
              </select>
              <select
                value={year}
                onChange={e => setYear(Number(e.target.value))}
                className="text-xs font-semibold text-slate-700 bg-transparent border-none outline-none cursor-pointer"
              >
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>

            <button
              type="button"
              onClick={nextMonth}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors flex-shrink-0"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-1">
            {DAYS.map(d => (
              <div key={d} className="text-center text-[10px] font-medium text-slate-400 py-1">{d}</div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7">
            {cells.map((day, i) => {
              if (!day) return <div key={`e-${i}`} className="h-8" />;
              const iso = toISO(year, month, day);
              const isSelected = iso === selected;
              const isToday = iso === today;
              const isDisabled = Boolean((minISO && iso < minISO) || (maxISO && iso > maxISO));

              let cls = "h-8 w-8 mx-auto flex items-center justify-center text-[11px] font-medium rounded-full transition-colors select-none ";
              if (isDisabled) {
                cls += "text-slate-300 cursor-not-allowed";
              } else if (isSelected) {
                cls += "bg-cyan-600 text-white font-semibold cursor-pointer";
              } else if (isToday) {
                cls += "text-cyan-600 font-bold ring-1 ring-cyan-300 hover:bg-cyan-50 cursor-pointer";
              } else {
                cls += "text-slate-700 hover:bg-slate-100 cursor-pointer";
              }

              return (
                <div key={iso} className="h-8 flex items-center justify-center">
                  <button
                    type="button"
                    disabled={isDisabled}
                    onClick={() => { emit(iso); setOpen(false); }}
                    className={cls}
                  >
                    {day}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Today shortcut */}
          <div className="mt-3 pt-3 border-t border-slate-100 flex justify-center">
            <button
              type="button"
              onClick={() => {
                const t = todayISO();
                if ((!minISO || t >= minISO) && (!maxISO || t <= maxISO)) {
                  const d = new Date();
                  setYear(d.getFullYear());
                  setMonth(d.getMonth());
                  emit(t);
                  setOpen(false);
                }
              }}
              className="text-xs text-cyan-600 font-medium hover:text-cyan-700 transition-colors"
            >
              Today
            </button>
          </div>
        </div>
      )}
    </>
  );
};
